import React, { createContext, useState, useContext, useEffect } from 'react';

interface AnimationsContextType {
  animationsEnabled: boolean;
  toggleAnimations: () => void;
  setAnimationsEnabled: (enabled: boolean) => void;
}

export const AnimationsContext = createContext<AnimationsContextType>({
  animationsEnabled: true,
  toggleAnimations: () => {},
  setAnimationsEnabled: () => {},
});

export const useAnimations = () => useContext(AnimationsContext);

interface AnimationsProviderProps {
  children: React.ReactNode;
}

export const AnimationsProvider: React.FC<AnimationsProviderProps> = ({ children }) => {
  // Load animation preference from localStorage
  const [animationsEnabled, setAnimationsEnabledState] = useState<boolean>(() => {
    const stored = localStorage.getItem('animationsEnabled');
    return stored !== null ? stored === 'true' : true; // Default to enabled
  });

  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('animationsEnabled', String(animationsEnabled));
  }, [animationsEnabled]);

  const toggleAnimations = () => {
    setAnimationsEnabledState((prev) => !prev);
  };

  const setAnimationsEnabled = (enabled: boolean) => {
    setAnimationsEnabledState(enabled);
  };

  return (
    <AnimationsContext.Provider value={{ animationsEnabled, toggleAnimations, setAnimationsEnabled }}>
      {children}
    </AnimationsContext.Provider>
  );
};

/**
 * Hook that returns animation styles or empty objects based on user preference
 */
export const useAnimationStyles = () => {
  const { animationsEnabled } = useAnimations();

  return {
    /**
     * Apply animation styles only if animations are enabled
     * @param styles - Animation styles object
     */
    apply: (styles: any) => (animationsEnabled ? styles : {}),
  };
};
